import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'
import axios from 'axios'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { useAuth } from '../contexts/AuthContext'
import { Member } from 'shared'
import StatsCard from './StatsCard'
import AddMemberModal from './AddMemberModal'
import MemberGrowthChart from './charts/MemberGrowthChart'
import PaymentDistributionChart from './charts/PaymentDistributionChart'
import FinancialTrendsChart from './charts/FinancialTrendsChart'

interface DashboardOverviewProps {
  onNavigateToMembers?: () => void
  onNavigateToPending?: () => void
  onNavigateToPayments?: () => void
  onNavigateToInvoices?: () => void
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ onNavigateToMembers, onNavigateToPending, onNavigateToPayments, onNavigateToInvoices }) => {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [showAddModal, setShowAddModal] = useState(false)
  const { logout } = useAuth()


  useEffect(() => {
    fetchMembers()
  }, [])

  const fetchMembers = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get(`${API_BASE_URL}/api/members`)
      setMembers(response.data.data?.members || [])
    } catch (err: any) {
      console.error('Failed to fetch members:', err)
      if (err.response?.status === 401) {
        logout()
      } else {
        setError(err.response?.data?.message || 'Failed to load members')
      }
    } finally {
      setLoading(false)
    }
  }

  const exportToCSV = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/members/export/csv`, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.download = `members_export_${timestamp}.csv`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Failed to export CSV:', err)
      if (err.response?.status === 401) {
        logout()
      } else {
        setError('Failed to export CSV file')
      }
    }
  }

  const memberStats = React.useMemo(() => {
    const memberList = members || []
    const total = memberList.length
    const active = memberList.filter(m => m.membershipStatus === 'ACTIVE').length
    const pending = memberList.filter(m => m.membershipStatus === 'PENDING').length
    const inactive = memberList.filter(m => m.membershipStatus === 'INACTIVE' || m.membershipStatus === 'SUSPENDED').length
    const activeWithIncome = memberList.filter(m => m.membershipStatus === 'ACTIVE' && m.earnsIncome).length

    // Calculate total monthly fees for members with income
    const totalMonthlyFees = memberList
      .filter(m => m.membershipStatus === 'ACTIVE' && m.earnsIncome)
      .reduce((sum, member) => {
        const fee = Number(member.membershipFee) || 0
        return sum + fee
      }, 0)

    const today = new Date()
    const currentQuarter = Math.floor(today.getMonth() / 3)
    const quarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1)

    const newThisQuarter = memberList.filter(m => {
      if (!m.joinDate) return false
      const joinDate = new Date(m.joinDate)
      return joinDate >= quarterStart
    }).length

    return { total, active, pending, inactive, activeWithIncome, totalMonthlyFees, newThisQuarter }
  }, [members])

  const recentMembers = React.useMemo(() => {
    return [...members]
      .sort((a, b) => new Date(b.joinDate || '').getTime() - new Date(a.joinDate || '').getTime())
      .slice(0, 5)
  }, [members])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  const nameBodyTemplate = (member: Member) => {
    return `${member.firstName} ${member.lastName}`
  }

  const statusBodyTemplate = (member: Member) => {
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold
        ${member.membershipStatus === 'ACTIVE' ? 'bg-green-100 text-green-800' :
          member.membershipStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'}`}>
        {member.membershipStatus}
      </span>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <div className="text-center p-6">
            <i className="pi pi-exclamation-triangle text-6xl text-red-500 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button
              label="Try Again"
              icon="pi pi-refresh"
              onClick={fetchMembers}
              className="p-button-outlined"
            />
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Statistics Overview - Compact for narrow screens */}
      <div className="block lg:hidden">
        <Card>
          <div className="grid grid-cols-3 gap-x-4 gap-y-3 sm:grid-cols-5">
            {[
              { label: 'Total', value: memberStats.total, dotColor: 'bg-blue-500', sub: memberStats.newThisQuarter > 0 ? `+${memberStats.newThisQuarter} this Q` : undefined },
              { label: 'Active', value: memberStats.active, dotColor: 'bg-green-500' },
              { label: 'Pending', value: memberStats.pending, dotColor: 'bg-orange-500', clickable: memberStats.pending > 0, onClick: onNavigateToPending },
              { label: 'Income', value: `${memberStats.activeWithIncome}`, dotColor: 'bg-purple-500', sub: `€${Number(memberStats.totalMonthlyFees || 0).toFixed(0)}` },
              { label: 'Inactive', value: memberStats.inactive, dotColor: 'bg-red-500' },
            ].map((item) => (
              <div
                key={item.label}
                className={`flex flex-col items-center text-center ${item.clickable ? 'cursor-pointer' : ''}`}
                onClick={item.clickable ? item.onClick : undefined}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`inline-block w-2 h-2 rounded-full ${item.dotColor}`} />
                  <span className="text-xs font-medium text-gray-500">{item.label}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{item.value}</span>
                {item.sub && <span className="text-xs text-gray-400">{item.sub}</span>}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Statistics Overview - Full cards for wide screens */}
      <div className="hidden lg:grid grid-cols-5 gap-6">
        <StatsCard
          title="Total Members"
          value={memberStats.total}
          icon="pi pi-users"
          color="blue"
          trend={memberStats.newThisQuarter > 0 ? {
            value: `+${memberStats.newThisQuarter} this quarter`,
            isPositive: true
          } : undefined}
        />
        <StatsCard
          title="Active Members"
          value={memberStats.active}
          icon="pi pi-check-circle"
          color="green"
        />
        <StatsCard
          title="Pending Approval"
          value={memberStats.pending}
          icon="pi pi-clock"
          color="orange"
          clickable={memberStats.pending > 0}
          onClick={memberStats.pending > 0 ? onNavigateToPending : undefined}
        />
        <StatsCard
          title="With Income"
          value={`${memberStats.activeWithIncome || 0} (€${Number(memberStats.totalMonthlyFees || 0).toFixed(0)})`}
          icon="pi pi-dollar"
          color="purple"
        />
        <StatsCard
          title="Inactive"
          value={memberStats.inactive}
          icon="pi pi-times-circle"
          color="red"
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="hidden sm:block">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Quick Actions</h3>
            <p className="text-gray-600">Common tasks and operations</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              label="Add Member"
              icon="pi pi-user-plus"
              className="p-button-success p-button-sm sm:p-button-md flex-1 sm:flex-none"
              onClick={() => setShowAddModal(true)}
            />
            <Button
              icon="pi pi-download"
              className="p-button-outlined p-button-sm sm:p-button-md sm:hidden"
              onClick={exportToCSV}
              disabled={members.length === 0}
              tooltip="Export Data"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              label="Export Data"
              icon="pi pi-download"
              className="p-button-outlined p-button-sm sm:p-button-md hidden sm:inline-flex"
              onClick={exportToCSV}
              disabled={members.length === 0}
            />
            <Button
              icon="pi pi-euro"
              className="p-button-outlined p-button-sm sm:p-button-md sm:hidden"
              onClick={onNavigateToPayments}
              tooltip="Payments"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              label="Manage Payments"
              icon="pi pi-euro"
              className="p-button-outlined p-button-sm sm:p-button-md hidden sm:inline-flex"
              onClick={onNavigateToPayments}
            />
            <Button
              icon="pi pi-file-pdf"
              className="p-button-outlined p-button-sm sm:p-button-md sm:hidden"
              onClick={onNavigateToInvoices}
              tooltip="Invoices"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              label="Manage Invoices"
              icon="pi pi-file-pdf"
              className="p-button-outlined p-button-sm sm:p-button-md hidden sm:inline-flex"
              onClick={onNavigateToInvoices}
            />
          </div>
        </div>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Member Growth Chart */}
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Member Growth</h3>
            <p className="text-gray-600">Member growth over the last 8 quarters</p>
          </div>
          <MemberGrowthChart />
        </Card>

        {/* Payment Distribution Chart */}
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Payment Distribution</h3>
            <p className="text-gray-600">Current year income by type</p>
          </div>
          <PaymentDistributionChart />
        </Card>
      </div>

      {/* Financial Trends Chart */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Financial Trends</h3>
            <p className="text-gray-600">Income vs expenses over the last 8 quarters</p>
          </div>
          <FinancialTrendsChart />
        </Card>
      </div>

      {/* Recent Members and Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Members */}
        <Card>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Recent Members</h3>
              <p className="text-gray-600">Latest member registrations</p>
            </div>
            <Button
              label="View All"
              icon="pi pi-external-link"
              className="p-button-text p-button-sm"
              onClick={onNavigateToMembers}
            />
          </div>
          <DataTable
            value={recentMembers}
            loading={loading}
            className="p-datatable-sm"
            emptyMessage="No members found"
          >
            <Column
              header="Name"
              body={nameBodyTemplate}
              style={{ minWidth: '120px' }}
            />
            <Column
              field="email"
              header="Email"
              style={{ minWidth: '150px' }}
            />
            <Column
              header="Status"
              body={statusBodyTemplate}
              style={{ minWidth: '80px' }}
            />
            <Column
              field="joinDate"
              header="Join Date"
              body={(member) => member.joinDate ? formatDate(member.joinDate) : '-'}
              style={{ minWidth: '100px' }}
            />
          </DataTable>
        </Card>

        {/* Member Status Distribution */}
        <Card>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Member Distribution</h3>
            <p className="text-gray-600">Overview of member status</p>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                <span className="font-medium text-gray-700">Active</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-green-600">{memberStats.active}</span>
                <span className="text-sm text-gray-500 ml-2">
                  {memberStats.total > 0 ? Math.round((memberStats.active / memberStats.total) * 100) : 0}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                <span className="font-medium text-gray-700">Inactive</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-red-600">{memberStats.inactive}</span>
                <span className="text-sm text-gray-500 ml-2">
                  {memberStats.total > 0 ? Math.round((memberStats.inactive / memberStats.total) * 100) : 0}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                <span className="font-medium text-gray-700">With Income (Active)</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-blue-600">{memberStats.activeWithIncome}</span>
                <span className="text-sm text-gray-500 ml-2">
                  {memberStats.active > 0 ? Math.round((memberStats.activeWithIncome / memberStats.active) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <AddMemberModal
        visible={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchMembers()
          setShowAddModal(false)
        }}
      />
    </div>
  )
}

export default DashboardOverview